import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { normalizeBarcode } from "../features/scanner";
import { useHtml5Qr } from "../features/scanerHook";

export function useScanner(onDecoded: (code: string) => void) {
  const regionRef = useRef<HTMLDivElement | null>(null);
  const [isScanning, setIsScanning] = useState(false);

  const processing = useRef(false);
  const { start, stop } = useHtml5Qr("qr-region", handleSuccess);
  const stopping = useRef(false);

  async function stopScanSafe() {
    if (stopping.current) return;
    stopping.current = true;
    try {
      await stop();
    } finally {
      stopping.current = false;
      setIsScanning(false);
    }
  }

  async function handleSuccess(raw: string) {
    const code = normalizeBarcode(raw);
    if (!code || processing.current) return;
    processing.current = true;
    onDecoded(code);
    setTimeout(() => (processing.current = false), 600);
  }

  useEffect(() => {
    if (!isScanning || !regionRef.current) return;

    (async () => {
      try {
        await new Promise<void>((res) =>
          requestAnimationFrame(() =>
            regionRef.current!.offsetWidth ? res() : setTimeout(res, 50)
          )
        );
        await start();
      } catch (e) {
        setIsScanning(false);
        console.error(e);
        toast.error("Не удалось открыть камеру");
      }
    })();

    return () => void stopScanSafe();
  }, [isScanning, start]);

  useEffect(() => {
    if (!isScanning || !regionRef.current) return;
    requestAnimationFrame(() =>
      regionRef.current!.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  }, [isScanning]);

  useEffect(() => {
    const h = () =>
      document.visibilityState === "hidden" && stopScanSafe();
    window.addEventListener("visibilitychange", h);
    window.addEventListener("pagehide", stopScanSafe);
    return () => {
      window.removeEventListener("visibilitychange", h);
      window.removeEventListener("pagehide", stopScanSafe);
    };
  }, []);

  return {
    isScanning,
    startScan: () => setIsScanning(true),
    stopScan: stopScanSafe,
    regionRef,
  };
}
